import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {makeStyles} from '@material-ui/core/styles'
import {
    Box,
    CssBaseline,
    Button,
    IconButton,
    TextField,
    Typography,
} from '@material-ui/core'
import Close from '@material-ui/icons/Close'
import MaskedInput from 'react-text-mask'
import {Formik, Form} from 'formik'
import {connect} from 'react-redux'
import {dataActions} from '../../actions'
import CircularProgress from '@material-ui/core/CircularProgress'

// using hook API
const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
    },
    lookupcontainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        maxWidth: '425px',
        padding: '2% 3% 0 3%',
    },
    submitButton: {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary.main,
        margin: theme.spacing(3, 0, 2),
        boxShadow: 'none',
        borderRadius: theme.shape.borderRadius,
        height: '2.625rem',
        textTransform: 'uppercase',
        width: '100%',
        '&:hover': {
            boxShadow: 'none',
            backgroundColor: '#00652A',
        },
    },
}))

function TextMaskCustom(props) {
    const {inputRef, ...other} = props

    return (
        <MaskedInput
            {...other}
            ref={ref => {
                inputRef(ref ? ref.inputElement : null)
            }}
            mask={[
                /[01]/,
                /[0-9]/,
                '/',
                /[0123]/,
                /[0-9]/,
                '/',
                /[12]/,
                /\d/,
                /\d/,
                /\d/,
            ]}
            showMask={false}
        />
    )
}

TextMaskCustom.propTypes = {
    inputRef: PropTypes.func.isRequired,
}

const LookupSearch = props => {
    const classes = useStyles()
    const {
        swipe,
        token,
        lookupParams,
        lookupPerson,
        callbackfunction,
        handleClose,
        occurrence,
        lookupresult,
        lookupEUIDPerson,
        fetchingLookupResults,
        exactMatchFn,
    } = props

    const {eventId, occurId} = occurrence

    const eightDigitRegex = new RegExp('(\\D*\\d){8}')

    const handleSubmit = async (token, values) => {
        await lookupPerson(token, values)
    }

    const validateDOB = dateofbirth => {
        const dateRegex = new RegExp(
            '(0?[1-9]|1[012])/(0?[1-9]|[12][0-9]|3[01])/((19|20)\\d\\d)'
        )
        return dateRegex.test(dateofbirth)
    }

    // Add lookup result listener
    useEffect(() => {
        if (Array.isArray(lookupresult) && lookupresult.length === 1) {
            switch (exactMatchFn) {
                case 'swipe':
                    swipe(token, eventId, occurId, lookupresult[0].emplid)
                    break
                case 'addUser':
                case 'addSwiper':
                case 'addSuperadmin':
                    lookupEUIDPerson(token, lookupresult[0].euid)
                    break
                default:
                    break
            }
            handleClose()
        } else {
            if (Array.isArray(lookupresult) && lookupresult.length !== 1) {
                callbackfunction('ReviewResults')
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lookupresult])

    return (
        <>
            <CssBaseline />
            <div className={classes.root}>
                <div style={{align: 'right'}}>
                    <IconButton onClick={handleClose}>
                        <Close style={{color: '#000000'}} />
                    </IconButton>
                </div>
                <Box className={classes.lookupcontainer}>
                    <Typography align="left" variant="h5">
                        Find by Name
                    </Typography>
                    <Typography align="left" variant="body1">
                        Ask attendee for their name that’s on file with the
                        university. Don’t use nicknames.
                    </Typography>

                    <Formik
                        initialValues={
                            lookupParams
                                ? lookupParams
                                : {
                                      firstName: '',
                                      lastName: '',
                                      monthDay: '',
                                  }
                        }
                        onSubmit={values => handleSubmit(token, values)}
                    >
                        {props => (
                            <>
                                <Form onSubmit={props.handleSubmit}>
                                    <TextField
                                        id="firstName"
                                        name="firstName"
                                        label="First Name"
                                        required
                                        fullWidth
                                        autoFocus
                                        autoComplete="off"
                                        margin="normal"
                                        variant="filled"
                                        value={props.values.firstName || ''}
                                        onBlur={props.handleBlur}
                                        onChange={props.handleChange}
                                        disabled={fetchingLookupResults}
                                    />
                                    <TextField
                                        id="lastName"
                                        name="lastName"
                                        label="Last Name"
                                        required
                                        fullWidth
                                        autoComplete="off"
                                        margin="normal"
                                        variant="filled"
                                        value={props.values.lastName || ''}
                                        onBlur={props.handleBlur}
                                        onChange={props.handleChange}
                                        disabled={fetchingLookupResults}
                                    />
                                    <TextField
                                        id="monthDay"
                                        name="monthDay"
                                        label="Birthday"
                                        fullWidth
                                        autoComplete="off"
                                        margin="normal"
                                        variant="filled"
                                        placeholder="MM/DD/YYYY"
                                        InputProps={{
                                            inputComponent: TextMaskCustom,
                                            value: props.values.monthDay || '',
                                            onChange: props.handleChange,
                                        }}
                                        onBlur={props.handleBlur}
                                        onChange={props.handleChange}
                                        disabled={fetchingLookupResults}
                                        error={
                                            eightDigitRegex.test(
                                                props.values.monthDay
                                            ) &&
                                            !validateDOB(props.values.monthDay)
                                        }
                                        helperText={
                                            eightDigitRegex.test(
                                                props.values.monthDay
                                            ) &&
                                            !validateDOB(
                                                props.values.monthDay
                                            ) &&
                                            'Invalid Date of Birth'
                                        }
                                    />
                                    {props.isSubmitting ? (
                                        <div align="center">
                                            <CircularProgress
                                                style={{
                                                    color: '#00853e',
                                                }}
                                                thickness={3}
                                            />
                                        </div>
                                    ) : (
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="medium"
                                            disabled={
                                                !props.dirty ||
                                                props.isSubmitting ||
                                                !props.touched ||
                                                (props.values.monthDay &&
                                                    !eightDigitRegex.test(
                                                        props.values.monthDay
                                                    )) ||
                                                (eightDigitRegex.test(
                                                    props.values.monthDay
                                                ) &&
                                                    !validateDOB(
                                                        props.values.monthDay
                                                    ))
                                            }
                                            className={classes.submitButton}
                                        >
                                            Search
                                        </Button>
                                    )}
                                </Form>
                            </>
                        )}
                    </Formik>
                </Box>
            </div>
        </>
    )
}

function mapStateToProps(state) {
    return {
        token: state.authentication.token,
        lookupParams: state.lookupdata.lookupParams,
        lookupresult: state.lookupdata.lookupresult,
        occurrence: state.occurrencedata.occurrence,
        fetchingLookupResults: state.lookupdata.fetchingLookupResults,
    }
}

const mapDispatchToProps = {
    lookupPerson: (token, values) => dataActions.lookupRequest(token, values),
    lookupEUIDPerson: (token, euid) =>
        dataActions.lookupEUIDRequest(token, euid),
    swipe: (token, eventId, occurId, emplId) =>
        dataActions.swipeRequest(token, eventId, occurId, emplId),
}

export default connect(mapStateToProps, mapDispatchToProps)(LookupSearch)
